package com.siriusxm.pia.components

import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.plus
import kotlin.js.Date
import kotlin.time.Clock
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.nanoseconds
import kotlin.time.Instant

// Shared time-zone utilities for CMC applications. Originally ported into the EPG channel-guide and
// sports apps; promoted here so every app renders times in the same user-selectable, DST-correct
// way. Browser (Intl) interop lives here; the user-facing picker and persistence live in
// TimeZonePicker.kt.

@JsName("Intl")
external object Intl {
    fun DateTimeFormat(locales: String = definedExternally, options: dynamic = definedExternally): DateTimeFormat
}

external class DateTimeFormat(
    locales: String = definedExternally,
    options: dynamic = definedExternally
) {
    fun formatToParts(date: Date): Array<FormatPart>
    fun resolvedOptions(): ResolvedDateTimeFormatOptions
}

external interface FormatPart {
    val type: String
    val value: String
}

external interface ResolvedDateTimeFormatOptions {
    val timeZone: String
}

/**
 * IANA zone ID of the user's local time zone, e.g. "America/New_York".
 */
fun localTimeZoneId(): String = Intl.DateTimeFormat().resolvedOptions().timeZone

/**
 * Short, DST-aware abbreviation for an IANA zone ID at the current moment, e.g.
 * "EST", "EDT", "GMT+5:30". Falls back to the zone ID if the runtime cannot
 * produce a short name.
 */
fun timeZoneAbbreviation(zoneId: String): String = runCatching {
    val options = js("{}")
    options.timeZone = zoneId
    options.timeZoneName = "short"
    val parts = Intl.DateTimeFormat("en-US", options).formatToParts(Date())
    parts.firstOrNull { it.type == "timeZoneName" }?.value ?: zoneId
}.getOrDefault(zoneId)

/**
 * GMT offset string for an IANA zone ID at the current moment, e.g. "GMT-5",
 * "GMT+5:30". Uses `shortOffset` for compact display.
 */
fun timeZoneOffset(zoneId: String): String = runCatching {
    val options = js("{}")
    options.timeZone = zoneId
    options.timeZoneName = "shortOffset"
    val parts = Intl.DateTimeFormat("en-US", options).formatToParts(Date())
    parts.firstOrNull { it.type == "timeZoneName" }?.value.orEmpty()
}.getOrDefault("")

/**
 * DST-correct conversion of an [Instant] to [LocalDateTime] in the given IANA zone.
 *
 * Uses `Intl.DateTimeFormat.formatToParts` per-instant, so the offset is queried
 * at the moment the instant represents — not "now" — and DST transitions in the
 * visible window render correctly even without `@js-joda/timezone` on the classpath.
 *
 * **Display-only.** Millisecond precision is preserved (as nanoseconds), but the
 * underlying JS `Date`/`Intl` pipeline has no finer resolution than milliseconds, so
 * don't round-trip the value back into Instant-level math expecting nanosecond equality.
 */
fun Instant.toLocalDateTimeIn(zoneId: String): LocalDateTime {
    val date = Date(toEpochMilliseconds().toDouble())
    val parts = ldtFormatter(zoneId).formatToParts(date)
    var year = 1970; var month = 1; var day = 1
    var hour = 0; var minute = 0; var second = 0; var millis = 0
    parts.forEach { p ->
        when (p.type) {
            "year" -> year = p.value.toInt()
            "month" -> month = p.value.toInt()
            "day" -> day = p.value.toInt()
            // Some locales emit "24" for midnight; normalize.
            "hour" -> hour = p.value.toInt() % 24
            "minute" -> minute = p.value.toInt()
            "second" -> second = p.value.toInt()
            "fractionalSecond" -> millis = p.value.toInt()
        }
    }
    return LocalDateTime(year, month, day, hour, minute, second, millis * 1_000_000)
}

/** DST-correct date-only projection. See [toLocalDateTimeIn]. */
fun Instant.toLocalDateIn(zoneId: String): LocalDate = toLocalDateTimeIn(zoneId).date

/**
 * The Instant corresponding to wall-clock midnight on this date in the given zone.
 *
 * Uses Intl-derived offset at the date's noon UTC as a proxy for the offset at
 * local midnight. DST transitions virtually never occur at midnight local time,
 * so this is reliable across all curated zones.
 */
fun LocalDate.atStartOfDayInZone(zoneId: String): Instant {
    val midnightUtc = Instant.parse("${this}T00:00:00Z")
    val noonUtc = Instant.parse("${this}T12:00:00Z")
    val offsetMinutes = zoneOffsetMinutesAt(zoneId, noonUtc)
    return midnightUtc.minus(offsetMinutes.minutes)
}

/**
 * The last representable Instant of this date in the given zone — i.e. the next day's
 * wall-clock midnight, minus one nanosecond.
 *
 * Pairs with [atStartOfDayInZone] to turn a date-only selection into a closed, inclusive
 * Instant range covering the whole day. Inherits the DST correctness of [atStartOfDayInZone].
 */
fun LocalDate.atEndOfDayInZone(zoneId: String): Instant =
    plus(1, DateTimeUnit.DAY).atStartOfDayInZone(zoneId) - 1.nanoseconds

/**
 * The Instant at which the wall clock in the given zone reads this date and time.
 *
 * Two passes over the zone's offset: the first guesses using the offset at the wall-clock time
 * read as UTC, the second corrects with the offset at that guess, which is right whenever the
 * guess and the answer fall on the same side of a DST transition — every time except the hour a
 * transition skips or repeats, where it resolves to one of the two neighbouring instants.
 */
fun LocalDateTime.toInstantInZone(zoneId: String): Instant {
    // Built from fields, not by parsing "${this}Z": LocalDateTime.toString() drops zero seconds
    // ("2026-07-15T00:00"), which Instant.parse rejects.
    val asUtc = Instant.fromEpochSeconds(date.toEpochDays().toLong() * 86_400 + time.toSecondOfDay(), nanosecond)
    val guess = asUtc - zoneOffsetMinutesAt(zoneId, asUtc).minutes
    return asUtc - zoneOffsetMinutesAt(zoneId, guess).minutes
}

/** DST-correct "today" in the given zone. */
fun todayIn(zoneId: String): LocalDate = Clock.System.now().toLocalDateIn(zoneId)

private fun zoneOffsetMinutesAt(zoneId: String, instant: Instant): Int = runCatching {
    val date = Date(instant.toEpochMilliseconds().toDouble())
    val parts = longOffsetFormatter(zoneId).formatToParts(date)
    val off = parts.firstOrNull { it.type == "timeZoneName" }?.value ?: "GMT"
    parseGmtOffsetMinutes(off)
}.getOrDefault(0)

// Intl.DateTimeFormat construction shows up in profiles when called from per-row
// display loops. The formatters are pure value objects keyed by (locale, options),
// so cache by the only varying input — zoneId — and reuse forever. Kotlin/JS is
// single-threaded.
private val ldtFormatterCache = mutableMapOf<String, DateTimeFormat>()
private val longOffsetFormatterCache = mutableMapOf<String, DateTimeFormat>()

private fun ldtFormatter(zoneId: String): DateTimeFormat = ldtFormatterCache.getOrPut(zoneId) {
    val options = js("{}")
    options.timeZone = zoneId
    options.year = "numeric"
    options.month = "2-digit"
    options.day = "2-digit"
    options.hour = "2-digit"
    options.minute = "2-digit"
    options.second = "2-digit"
    options.fractionalSecondDigits = 3
    options.hour12 = false
    Intl.DateTimeFormat("en-US", options)
}

private fun longOffsetFormatter(zoneId: String): DateTimeFormat = longOffsetFormatterCache.getOrPut(zoneId) {
    val options = js("{}")
    options.timeZone = zoneId
    options.timeZoneName = "longOffset"
    Intl.DateTimeFormat("en-US", options)
}

/**
 * Parse an Intl-style offset string into total minutes.
 *
 * Accepts the shapes `Intl.DateTimeFormat` actually emits: `"GMT±HH:MM"`,
 * `"GMT±HH"`, `"GMT±H"`, `"GMT"` (zero offset, some engines), and the bare
 * literals `"UTC"` / `"GMT"` that some locales return. Anything else returns 0.
 */
private fun parseGmtOffsetMinutes(s: String): Int {
    if (s == "GMT" || s == "UTC") return 0
    val rest = s.removePrefix("GMT").takeIf { it != s } ?: return 0
    if (rest.isEmpty()) return 0
    val sign = when (rest[0]) {
        '+' -> 1
        '-' -> -1
        else -> return 0
    }
    val core = rest.drop(1)
    val (h, m) = if (":" in core) {
        val parts = core.split(":")
        runCatching { parts[0].toInt() to parts.getOrElse(1) { "0" }.toInt() }
            .getOrElse { return 0 }
    } else {
        runCatching { core.toInt() to 0 }.getOrElse { return 0 }
    }
    return sign * (h * 60 + m)
}
